'use client';

import { useEffect, useRef, useState } from 'react';
import { FiChevronDown, FiSearch } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { transformSummary } from '../../../utlis/summaryhelpers';
import ActionBar from '../../reception/viewReceptionData/actionBar';
import ApplicantCard from '../../reception/viewReceptionData/applicantCard';
import FormSummary from '../../reception/viewReceptionData/formSummary';
import NotesBox from '../../reception/viewReceptionData/notesBox';
import SummaryTable from '../../reception/viewReceptionData/summaryTable';
import MarketingTable from './MarketingTable';
import LimitDropdown from '../../common/LimitDropdown';

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

export default function MainMarketing() {
  const navigate = useNavigate();
  const summaryRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('month');
  const [year, setYear] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [month, setMonth] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasNext, setHasNext] = useState(false);
  const [totalPages, setTotalPages] = useState(1);

  const [showSummary, setShowSummary] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState('');
  const [summaryData, setSummaryData] = useState([]);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('');
  const [isVisaDropdownOpen, setIsVisaDropdownOpen] = useState(false);
  const visaDropdownRef = useRef(null);
  const [selectedSummaryIndex, setSelectedSummaryIndex] = useState(0);
  const previousYearRef = useRef('');
  const skipNextEmptyYearFetchRef = useRef(false);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const handleYearChange = (value) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 4);
    setYear(digitsOnly);
    setCurrentPage(1);
  };

  const handleYearKeyDown = (e) => {
    if (["-", "+", ".", "e", "E"].includes(e.key)) {
      e.preventDefault();
    }
  };

  // Close visa dropdown on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (isVisaDropdownOpen && !visaDropdownRef.current?.contains(e.target)) {
        setIsVisaDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [isVisaDropdownOpen]);

  // 🔹 Fetch appointments directly inside component
  useEffect(() => {
    const previousYear = String(previousYearRef.current || '');
    const currentYear = String(year || '');
    const wasPreviousYearValid = /^[1-9]\d{3}$/.test(previousYear);
    const isCurrentYearValid = /^[1-9]\d{3}$/.test(currentYear);
    const isPartialYear = currentYear.length > 0 && currentYear.length < 4;
    const isEmptyYear = currentYear.length === 0;
    const droppedFromValidYear = wasPreviousYearValid && isPartialYear;

    previousYearRef.current = currentYear;

    if (isCurrentYearValid) {
      skipNextEmptyYearFetchRef.current = false;
    }

    if (droppedFromValidYear) {
      skipNextEmptyYearFetchRef.current = true;
    }

    if (isPartialYear && !droppedFromValidYear) {
      return;
    }

    if (isEmptyYear && previousYear.length > 0 && skipNextEmptyYearFetchRef.current) {
      skipNextEmptyYearFetchRef.current = false;
      return;
    }

    async function loadAppointments() {
      setLoading(true);
      setError('');

      try {
        // Build query string manually to ensure spaces are %20 and omit name when empty
        const qs = [];
        qs.push(`page=${encodeURIComponent(String(currentPage))}`);
        qs.push(`limit=${encodeURIComponent(String(itemsPerPage))}`);
        // Only add year if user has entered a complete 4-digit year
        if (/^[1-9]\d{3}$/.test(String(year))) {
          qs.push(`year=${encodeURIComponent(String(year))}`);
        }
        // Only add month if user has selected one
        if (month) {
          qs.push(`month=${encodeURIComponent(String(month))}`);
        }
        if (searchTerm && String(searchTerm).trim().length > 0) {
          qs.push(`name=${encodeURIComponent(String(searchTerm).trim())}`);
        }
        if (selectedFilter) {
          qs.push(
            `visaCategory=${encodeURIComponent(String(selectedFilter).trim())}`,
          );
        }
        const queryString = qs.join('&');

        const res = await fetch(
          `${API_BASE}/api/appointments/getAppointments?${queryString}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
            },
          },
        );

        if (!res.ok) throw new Error(`Error ${res.status}`);

        const data = await res.json();

        // ✅ Correct extraction
        const list = data?.body?.appointments || [];
        const pagination = data?.body?.pagination || {};

        setRows(
          list.map((a) => ({
            id: a._id,
            name: a.fullName || '',
            location: a.suburb || a.residentialAddress || '',
            visaType: a.visaCategory || '',
            phone: a.mobile || '',
            mobile: a.mobile || '',
            email: a.email || '',
            lastContacted: a.createdAt
              ? new Date(a.createdAt).toLocaleDateString()
              : '',
          })),
        );

        setHasNext(
          typeof pagination.hasNextPage === 'boolean'
            ? pagination.hasNextPage
            : list.length === itemsPerPage,
        );

        // Track total pages if provided by API, otherwise derive when possible
        const tp =
          typeof pagination.totalPages === 'number' && pagination.totalPages > 0
            ? pagination.totalPages
            : typeof pagination.totalRecords === 'number' &&
                pagination.totalRecords >= 0
              ? Math.max(1, Math.ceil(pagination.totalRecords / itemsPerPage))
              : 0;
        setTotalPages(tp || 1);

        // Set totalRecords from pagination if available; otherwise derive
        const tr =
          typeof pagination.totalRecords === 'number' && pagination.totalRecords >= 0
            ? pagination.totalRecords
            : list.length === itemsPerPage
            ? currentPage * itemsPerPage
            : (currentPage - 1) * itemsPerPage + list.length;
        setTotalRecords(tr);
      } catch (err) {
        console.error('Fetch appointments error:', err);
        setError('Failed to load appointments.');
      } finally {
        setLoading(false);
      }
    }

    loadAppointments();
  }, [currentPage, searchTerm, year, month, selectedFilter, itemsPerPage]);

  // When a different summary is selected, scroll its container to top
  useEffect(() => {
    try {
      const el = summaryRef?.current;
      if (el && typeof el.scrollTo === 'function') {
        el.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch {}
  }, [selectedSummaryIndex]);

  // 🔹 Fetch summaries directly here too
  const onViewSummary = async (inquiry) => {
    setSelectedInquiry(inquiry);
    setShowSummary(true);
    setSummaryLoading(true);
    setSummaryError('');
    setSelectedSummaryIndex(0);

    try {
      // 🧠 Normalize mobile number
      let mobile = inquiry.mobile || inquiry.phone || '';

      // If it doesn’t start with +, add it
      if (mobile && !mobile.startsWith('+')) {
        mobile = '+' + mobile;
      }

      // Build encoded URL safely
      const res = await fetch(
        `${API_BASE}/api/appointments/allSummaries?mobile=${encodeURIComponent(mobile)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
          },
        },
      );

      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data?.body?.summaries)
        ? data.body.summaries.slice()
        : [];
      // sort by created_at desc if present
      list.sort((a, b) => {
        const da = a?.created_at ? new Date(a.created_at).getTime() : 0;
        const db = b?.created_at ? new Date(b.created_at).getTime() : 0;
        return db - da;
      });
      setSummaryData(list);
    } catch (err) {
      console.error('Summary fetch error:', err);
      setSummaryError('Failed to load summaries.');
    } finally {
      setSummaryLoading(false);
    }
  };

  const handlePrintSummary = () => {
    try {
      const node = summaryRef.current;
      if (!node) return;
      const content = node.innerHTML;
      const w = window.open('', '_blank', 'width=1024,height=768');
      if (!w) return;
      w.document.open();
      w.document.write(`<!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <script src="https://cdn.tailwindcss.com"></script>
            <style>
              @page { size: A4; margin: 16mm; }
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            </style>
            <title>Summary</title>
          </head>
          <body class="bg-white">
            <div class="max-w-3xl mx-auto">${content}</div>
            <script>
              setTimeout(function(){ window.print(); window.close(); }, 400);
            <\/script>
          </body>
        </html>`);
      w.document.close();
    } catch (e) {
      console.error('Print error', e);
    }
  };

  const handlePageChange = (newPage) => {
    // If totalPages is known, clamp within [1, totalPages]
    if (totalPages && totalPages > 1) {
      const target = Math.min(Math.max(newPage, 1), totalPages);
      if (target !== currentPage) setCurrentPage(target);
      return;
    }
    // Fallback to previous/next when totalPages is unknown
    const delta = newPage - currentPage;
    const next = currentPage + (delta === 0 ? 0 : Math.sign(delta));
    if (next < 1) return;
    if (delta > 0 && !hasNext) return;
    if (next !== currentPage) setCurrentPage(next);
  };

  const showPaginationControls = totalRecords > itemsPerPage && totalPages > 1;

  return (
    <div className="md:p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-normal md:text-3xl">
          Marketing Dashboard
        </h1>
        <div className="relative w-full sm:w-auto sm:flex-initial max-w-md">

          <input
            type="text"
            placeholder="Search applicants"
            className="w-full md:w-[400px] rounded-full border text-base md:text-xl border-gray-300 py-2.5 pl-6 pr-4 focus:border-blue-500 focus:outline-none"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
          <FiSearch className="absolute right-4 top-1/2 -translate-y-1/2 font-bold text-xl text-gray-400" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 w-full">
        {/* Quick Actions Card */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6 shadow-sm">
          <h2 className="text-lg font-medium mb-4">Quick Actions</h2>
          <button
            onClick={() => navigate('/agent/marketing/campaign-create')}
            className="w-full bg-[#005787] text-white py-2.5 px-4 rounded-md hover:bg-[#004567] transition-colors"
          >
            Launch New Campaign
          </button>
        </div>

        {/* Campaigns Card */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6 shadow-sm">
          <h2 className="text-lg font-medium mb-4">Campaigns</h2>
          <button
            onClick={() => navigate('/agent/marketing/campaigns')}
            className="w-full bg-[#005787] text-white py-2.5 px-4 rounded-md hover:bg-[#004567] transition-colors"
          >
            View All Campaigns
          </button>
        </div>
      </div>
      {/* All Inquiries Section */}
      <div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-4">
          <h2 className="text-xl font-medium">All Inquiries - Walk In</h2>
        </div>

        {/* Filters: Year/Month + Visa Type dropdown */}
        <div className="mb-6 space-y-3">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Year</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[1-9][0-9]{3}"
                placeholder="YYYY"
                className="w-24 p-2 border border-gray-300 rounded-md text-sm"
                value={year}
                maxLength={4}
                onKeyDown={handleYearKeyDown}
                onChange={(e) => handleYearChange(e.target.value)}
              />
            </div>
            {viewMode === 'month' && (
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">Month</label>
                <select
                  className="p-2 border border-gray-300 rounded-md text-sm"
                  value={month}
                  onChange={(e) => {
                    setMonth(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">Select Month</option>
                  {MONTHS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="ml-5 relative" ref={visaDropdownRef}>
              <button
                type="button"
                onClick={() => setIsVisaDropdownOpen((v) => !v)}
                className="flex items-center justify-between min-w-[220px] px-4 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
              >
                <span className="text-sm text-gray-700">
                  {selectedFilter ? selectedFilter : 'Select Visa Type'}
                </span>
                <FiChevronDown
                  className={`ml-2 transition-transform ${isVisaDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {isVisaDropdownOpen && (
                <div className="absolute z-20 mt-2 w-[360px] bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-auto">
                  <div className="py-1 divide-y divide-gray-200">
                    <button
                      onClick={() => {
                        setSelectedFilter('');
                        setIsVisaDropdownOpen(false);
                        setCurrentPage(1);
                      }}
                      className={`w-full text-left px-4 py-2 hover:bg-gray-50 ${selectedFilter === '' ? 'bg-gray-50' : ''}`}
                    >
                      All Visa Types
                    </button>
                    {[
                      ...new Set(rows.map((r) => r.visaType).filter(Boolean)),
                    ].map((filter) => (
                      <button
                        key={filter}
                        onClick={() => {
                          setSelectedFilter(filter);
                          setIsVisaDropdownOpen(false);
                          setCurrentPage(1);
                        }}
                        className={`w-full text-left px-4 py-2 hover:bg-gray-50 ${selectedFilter === filter ? 'bg-gray-50' : ''}`}
                      >
                        {filter}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Table */}
        <MarketingTable
          inquiries={rows}
          onView={onViewSummary}
          loading={loading}
          error={error}
        />

        {/* Pagination - Matches leads and clientHistory UI */}
        <div className="flex items-center justify-center mt-6">
            {showPaginationControls && (
              <div className="flex items-center justify-center gap-2">
            {/* Previous Button */}
            <button
              onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
              disabled={currentPage === 1 || loading}
              className={`px-2 md:px-3 py-2 rounded-md ${
                currentPage === 1 || loading
                  ? 'bg-gray-200/50 text-gray-400 cursor-default'
                  : 'bg-gray-100 text-[#005787] hover:bg-gray-300'
              }`}
            >
              &lt;
            </button>

            {/* Page Numbers */}
            {Array.from({ length: totalPages }).map((_, index) => {
              const pageNumber = index + 1;
              if (
                pageNumber === 1 ||
                pageNumber === totalPages ||
                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
              ) {
                return (
                  <button
                    key={pageNumber}
                    onClick={() => handlePageChange(pageNumber)}
                    className={`px-3 md:px-4 py-1.5 md:py-2 rounded-md border ${
                      currentPage === pageNumber
                        ? 'bg-[#005787] text-white border-[#334155]'
                        : 'bg-gray-100 text-[#005787] hover:bg-gray-300 border-gray-200'
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              }
              if (
                pageNumber === currentPage - 2 ||
                pageNumber === currentPage + 2
              ) {
                return (
                  <span key={pageNumber} className="px-2 py-1 text-gray-500">
                    ...
                  </span>
                );
              }
              return null;
            })}

            {/* Next Button */}
            <button
              onClick={() =>
                handlePageChange(Math.min(currentPage + 1, totalPages))
              }
              disabled={currentPage === totalPages || loading}
              className={`px-2 md:px-3 py-2 rounded-md ${
                currentPage === totalPages || loading
                  ? 'bg-gray-200/50 text-gray-400 cursor-default'
                  : 'bg-gray-100 text-[#005787] hover:bg-gray-300'
              }`}
            >
              &gt;
            </button>
              </div>
            )}
            <div className={`hidden md:flex items-center gap-3 ${showPaginationControls ? 'ml-4' : ''}`}>
              {showPaginationControls && (
                <span className="text-sm text-gray-600">Total records: {totalRecords}</span>
              )}
              <LimitDropdown
                value={itemsPerPage}
                onChange={(v) => { setItemsPerPage(v); setCurrentPage(1); }}
              />
            </div>
        </div>
      </div>

      {/* Summary Modal */}
      {showSummary && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4 md:p-0">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] md:max-h-[80vh] p-4 md:p-6 flex flex-col">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 md:mb-6">
              <h3 className="text-lg md:text-xl font-medium">Form Summary</h3>
              <div className="flex items-center gap-3 sm:gap-4">
                {/* <button
                  onClick={handlePrintSummary}
                  className="flex-1 sm:flex-none px-3 md:px-4 py-2 md:py-2.5 rounded-md text-xs md:text-sm font-medium bg-[#005787] text-white hover:bg-[#004567] transition-colors whitespace-nowrap"
                >
                  Download Summary
                </button> */}
                <button
                  onClick={() => {
                    setShowSummary(false);
                    setSummaryData([]);
                    setSelectedInquiry(null);
                  }}
                  className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
                >
                  ✕
                </button>
              </div>
            </div>
            {summaryLoading ? (
              <div className="flex-1 flex items-center justify-center p-6 text-center text-gray-500">
                Loading...
              </div>
            ) : summaryError ? (
              <div className="flex-1 flex items-center justify-center p-6 text-center text-red-600">
                {summaryError}
              </div>
            ) : summaryData.length === 0 ? (
              <div className="flex-1 flex items-center justify-center p-6 text-center text-gray-500">
                No summaries found
              </div>
            ) : (
              <div
                className="flex-1 overflow-y-auto space-y-4 md:space-y-6 pr-2"
                ref={summaryRef}
              >
                {(() => {
                  const currentSummary = transformSummary(
                    summaryData[selectedSummaryIndex] || {},
                  );
                  const a =
                    summaryData[selectedSummaryIndex].appointmentId || {};
                  return (
                    <div className="space-y-6 w-full">
                      <ActionBar
                        isRecentSummaryView={true}
                        onBack={() => {
                          setShowSummary(false);
                          setSummaryData([]);
                          setSelectedInquiry(null);
                        }}
                        handleDownloadPDF={handlePrintSummary}
                      />
                      <ApplicantCard
                        fullName={a.fullName || ''}
                        visaType={a.visaCategory || ''}
                        visaStatus={a.visaStatus || ''}
                        nationality={a.nationality || ''}
                        currentResidence={
                          a.residentialAddress || a.suburb || ''
                        }
                        phone={a.mobile || ''}
                        email={a.email || ''}
                        country={a?.country || ''}
                        city={a?.city || ''}
                        suburb={a?.suburb || ''}
                        visaDetails={a?.otherVisaCategory || ''}
                      />

                      <div>
                        <FormSummary
                          totalPoints={currentSummary?.totalPoints}
                        />
                        <SummaryTable
                          age={currentSummary?.age}
                          englishLanguage={currentSummary?.englishLanguageScore}
                          dependent={currentSummary?.dependent}
                          highestQualification={
                            currentSummary?.highestEducationLevel
                          }
                          isCurrentlyStudying={currentSummary?.isStudying}
                          skillAssessment={currentSummary?.skillAssessment}
                          meetAustralianStudyRequirements={
                            currentSummary?.meetAustralianStudyRequirements
                          }
                          naati={currentSummary?.naatiAccredited}
                          maritalStatus={currentSummary?.maritalStatus}
                          partner={currentSummary?.partnerSkills}
                          employed={currentSummary?.employment}
                          purpose={currentSummary?.purposeOfVisit}
                          agePoints={currentSummary.agePoints}
                          overseasEmployement={
                            currentSummary.overseasEmployement
                          }
                          australianSkilledEmployment={
                            currentSummary.australianSkilledEmployment
                          }
                          isCompletedProfessionalYearAus={
                            currentSummary.isCompletedProfessionalYearAus
                          }
                          courses={currentSummary?.courses || ''}
                          englishLanguagePoints={
                            currentSummary.englishLanguagePoints
                          }
                          highestEducationPoints={
                            currentSummary.highestEducationPoints
                          }
                          meetAustralianStudyPoints={
                            currentSummary?.meetAustralianStudyPoints
                          }
                          masterResearchPoints={
                            currentSummary.masterResearchPoints
                          }
                          nattiPoints={currentSummary.nattiPoints}
                          regionalAustralianQualification={
                            currentSummary.regionalAustralianQualification
                          }
                          partnerPoints={currentSummary.partnerPoints}
                          overseasEmployementPoints={
                            currentSummary.overseasEmployementPoints
                          }
                          overseasJobTitle={currentSummary.overseasJobTitle}
                          overseasJobCompanyName={
                            currentSummary.overseasJobCompanyName
                          }
                          overseasJobCompanyCountry={
                            currentSummary.overseasJobCompanyCountry
                          }
                          overseasJobCompanyCity={
                            currentSummary.overseasJobCompanyCity
                          }
                          overseasJobDurationFrom={
                            currentSummary.overseasJobDurationFrom
                          }
                          overseasJobDurationTo={
                            currentSummary.overseasJobDurationTo
                          }
                          overseasJobIsWorking={
                            currentSummary.overseasJobIsWorking
                          }
                          australianSkilledEmploymentPoints={
                            currentSummary?.australianEmploymentPoints
                          }
                          regionalAustralianQualificationPoints={
                            currentSummary.regionalAustralianQualificationPoints
                          }
                          isCompletedProfessionalYearAusPoints={
                            currentSummary.isCompletedProfessionalYearAusPoints
                          }
                          currentAgent={currentSummary.currentAgent}
                          university={currentSummary.university}
                          degree={currentSummary.degree}
                        />
                        <NotesBox
                          personalDetailsNote={a.personalInfoNote || ''}
                          visaInformationNote={a.visaNote || ''}
                          australianEducationNote={
                            a.australianEducationNote || ''
                          }
                        />
                        {/* Previous summaries list with View buttons */}
                        {Array.isArray(summaryData) &&
                          summaryData.length > 1 && (
                            <div className="mt-6 md:mt-8 space-y-2 md:space-y-3">
                              {summaryData.map((itm, idx) => {
                                const isActive = idx === selectedSummaryIndex;
                                const dt = itm.created_at
                                  ? new Date(
                                      itm.created_at,
                                    ).toLocaleDateString()
                                  : '';
                                return (
                                  <div
                                    key={itm._id || idx}
                                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 rounded-xl border border-[#c9d7e5] bg-[#eaf3fb] px-3 md:px-4 py-3 md:py-3"
                                  >
                                    <div className="text-xs md:text-sm font-medium text-gray-800 min-w-0">
                                      <span>
                                        {idx === 0
                                          ? 'Last Visit Summary'
                                          : 'Visit Summary'}
                                      </span>
                                      {dt && (
                                        <span className="ml-2 text-gray-600 font-normal text-xs md:text-sm">
                                          {dt}
                                        </span>
                                      )}
                                    </div>
                                    <button
                                      onClick={() =>
                                        setSelectedSummaryIndex(idx)
                                      }
                                      disabled={isActive}
                                      className={`w-full sm:w-auto px-4 md:px-5 py-2 rounded-full text-white text-sm font-medium transition-colors whitespace-nowrap ${
                                        isActive
                                          ? 'bg-[#0a69a6] opacity-75 cursor-default'
                                          : 'bg-[#0d7ac2] hover:bg-[#0a69a6] active:bg-[#085393]'
                                      }`}
                                    >
                                      {isActive ? 'Viewing' : 'View'}
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
